/**
 * Mesh-Setup und Fit-Berechnung für ShipHologramPreview.
 */
import type { ParsedMesh } from '../renderer/GlbParser';

const TARGET_SIZE = 2.1;

export interface ShipPreviewFit {
  fitScale: number;
  centerY: number;
}

export function calcShipPreviewFit(positions: Float32Array): ShipPreviewFit {
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i], y = positions[i + 1], z = positions[i + 2];
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
    if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
  }
  const sx = maxX - minX, sy = maxY - minY, sz = maxZ - minZ;
  const maxDim = Math.max(sx, sy, sz, 0.001);
  const fitScale = TARGET_SIZE / maxDim;
  const centerY = -(minY + maxY) * 0.5 * fitScale;
  return { fitScale, centerY };
}

export interface ShipPreviewMeshResult {
  vao: WebGLVertexArrayObject;
  indexCount: number;
  indexType: number;
  buffers: WebGLBuffer[];
}

export function createShipPreviewMesh(
  gl: WebGL2RenderingContext,
  mesh: ParsedMesh,
): ShipPreviewMeshResult {
  const buffers: WebGLBuffer[] = [];
  const vao = gl.createVertexArray()!;
  gl.bindVertexArray(vao);

  uploadAttr(gl, buffers, mesh.positions, 3, 0);
  if (mesh.normals) {
    uploadAttr(gl, buffers, mesh.normals, 3, 1);
  } else {
    const n = new Float32Array(mesh.positions.length);
    for (let i = 1; i < n.length; i += 3) n[i] = 1;
    uploadAttr(gl, buffers, n, 3, 1);
  }
  if (mesh.uvs) uploadAttr(gl, buffers, mesh.uvs, 2, 2);

  let indexCount: number;
  let indexType: number;
  if (mesh.indices) {
    const buf = gl.createBuffer()!;
    buffers.push(buf);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, buf);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, mesh.indices, gl.STATIC_DRAW);
    indexCount = mesh.indices.length;
    indexType = mesh.indices instanceof Uint32Array ? gl.UNSIGNED_INT : gl.UNSIGNED_SHORT;
  } else {
    indexCount = mesh.positions.length / 3;
    indexType = 0;
  }
  gl.bindVertexArray(null);
  return { vao, indexCount, indexType, buffers };
}

function uploadAttr(
  gl: WebGL2RenderingContext,
  buffers: WebGLBuffer[],
  data: Float32Array,
  size: number,
  loc: number,
): void {
  const buf = gl.createBuffer()!;
  buffers.push(buf);
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0);
}
